//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import { useQuery } from '@tanstack/react-query';

import { searchLabels } from '@apis/gatewayMediaApi';
import {
  ISearchLabelsResponse,
  LabelsSortField,
} from '@apis/gatewayMediaApiModel';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { ErrorType, getErrorMessage } from '@utils/errorMessages';

const LABELS_BATCH_SIZE = 500;

interface SearchLabelsRecursivelyParams {
  accumulator?: ISearchLabelsResponse[];
  init?: RequestInit;
  page?: number;
}

const searchLabelsRecursively = async ({
  accumulator = [],
  init,
  page = 1,
}: SearchLabelsRecursivelyParams): Promise<ISearchLabelsResponse[]> => {
  const limit = LABELS_BATCH_SIZE;

  const searchResults = await searchLabels(
    [{ field: LabelsSortField.UserTag, ascending: true }],
    undefined, // We are fetching all labels, and the query should be undefined.
    limit * (page - 1),
    limit,
    init,
  );
  const newAccumulator = [...accumulator, searchResults];

  if (searchResults.data.indexObjects.length < limit) {
    return newAccumulator;
  } else {
    return searchLabelsRecursively({
      accumulator: newAccumulator,
      page: page + 1,
    });
  }
};

export const getAllLabels = async (init?: RequestInit) => {
  try {
    const response = await searchLabelsRecursively({ init });
    const acc: ISearchLabelsResponse = { data: { count: 0, indexObjects: [] } };

    return response.reduce((acc, next) => {
      return {
        data: {
          count: next.data.count,
          indexObjects: [...acc.data.indexObjects, ...next.data.indexObjects],
        },
      };
    }, acc);
  } catch (error) {
    throw new Error(getErrorMessage(ErrorType.LABELS_FETCH, error));
  }
};

export const useAllLabelsQuery = () => {
  return useQuery<ISearchLabelsResponse, Error>({
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersLabels,
      QueryKeyConstants.MediaFiltersLabelsManage,
    ],
    queryFn: ({ signal }) => getAllLabels({ signal }),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};
